import { Button, ButtonProps, Typography } from '@mui/material';
import React, { FC, ImgHTMLAttributes } from 'react';
import googleLogo from '../../../assets/google-logo.png';
import { useMutation } from 'react-query';
import { firebaseAuth } from '../../../shared/lib';
import {
  GoogleAuthProvider,
  UserCredential,
  signInWithCredential,
} from 'firebase/auth';
import { FirebaseError } from 'firebase/app';
import { LoggerService } from '../../../shared/logger';
import { FirebaseAuthentication } from '@capacitor-firebase/authentication';
import { Texts } from '../../../shared/types';

export const useLoginWithGoogle = () =>
  useMutation<UserCredential, FirebaseError>({
    mutationKey: 'login-with-google',
    mutationFn: async () => {
      const result = await FirebaseAuthentication.signInWithGoogle();
      const credential = GoogleAuthProvider.credential(
        result.credential?.idToken
      );
      return signInWithCredential(firebaseAuth, credential);
    },
  });

export const ContinueWithGoogleButton: FC = () => {
  const { mutate: loginWithGoogle, isLoading, isError } = useLoginWithGoogle();

  const handleLoginWithGoogle = () => {
    loginWithGoogle(undefined, {
      onError: error => {
        LoggerService.error('Error logging in with Google', error);
      },
    });
  };

  return (
    <ContinueWithButton
      {...{
        buttonProps: {
          onClick: handleLoginWithGoogle,
          disabled: isLoading,
        },
        imgProps: {
          src: googleLogo,
          alt: 'Google Logo',
          style: { width: 20, height: 20 },
        },
        isError,
      }}
    />
  );
};

interface ContinueWithButtonProps {
  buttonProps: ButtonProps;
  imgProps?: ImgHTMLAttributes<HTMLImageElement>;
  texts?: Texts<'title' | 'errorMessage'>;
  error?: FirebaseError | null;
  isError?: boolean;
}

export const ContinueWithButton: FC<ContinueWithButtonProps> = ({
  buttonProps,
  imgProps,
  texts = {
    title: 'Continue with Google',
    errorMessage: `Error signing in with Google, please try again.`,
  },
  isError = false,
}) => (
  <>
    <Button
      variant="outlined"
      fullWidth
      startIcon={imgProps ? <img alt="" {...imgProps} /> : undefined}
      {...buttonProps}
      sx={{
        // Google's sign-in guidance: neutral white button, dark label.
        bgcolor: '#FFFFFF',
        color: '#1F1F1F',
        borderColor: 'rgba(20, 35, 29, 0.24)',
        '&:hover': {
          bgcolor: '#F6F2EA',
          borderColor: 'rgba(20, 35, 29, 0.4)',
        },
        '&.Mui-disabled': { color: 'rgba(31,31,31,.5)', bgcolor: '#F1ECE2' },
        ...(buttonProps.sx as object),
      }}
    >
      {texts.title}
    </Button>
    {isError && (
      <Typography color="error.main" variant="body2" role="alert">
        {texts.errorMessage}
      </Typography>
    )}
  </>
);
